/*-
 * #%L
 * Sagae
 * %%
 * Copyright (C) 2020 - 2023 OFB
 * %%
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 * #L%
 */
import { ProjetAnnuelDTO, ProjetAnnuelLightDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import ConversionUtils from "@/services/ConversionUtils"
import { t } from "@/utils/i18n-utils"

// Type union pour les propriétés communes entre ProjetAnnuelDTO et ProjetAnnuelLightDTO
type ProjetAnnuelBase = ProjetAnnuelDTO | ProjetAnnuelLightDTO

export class CarnetExportResult {
  blob: Blob
  fileName: string | undefined
}

export class ProjectLabelsService {
  static INSTANCE = new ProjectLabelsService()

  getProjectStatusLabel(projetAnnuel: ProjetAnnuelBase, loggedUser: UtilisateurDTO): string {
    switch (loggedUser.profil) {
      case "REFERENT":
      case "ENSEIGNANT":
        switch (projetAnnuel.typeDossier) {
          // For PP dossier inscrition
          case "DOSSIER_INSCRIPTION":
            switch (projetAnnuel.statut) {
              case "BROUILLON":
                return t("projet.status-label.INSCRIPTION_BROUILLON")
              case "INSTRUCTION_TERMINEE":
              case "INSTRUCTION_EN_COURS":
              case "EN_ATTENTE":
                return t("projet.status-label.INSCRIPTION_ETUDE")
              case "ACCEPTE":
                return t("projet.status-label.INSCRIPTION_VALIDE")
              case "ACCEPTE_SOUS_RESERVE_REFERENT":
                return t("projet.status-label.INSCRIPTION_SOUS_RESERVE")
              case "REFUSE":
                return t("projet.status-label.INSCRIPTION_REFUSE")
              default:
                return projetAnnuel.statut
            }

          // For PP labellisation
          default:
            switch (projetAnnuel.statut) {
              case "BROUILLON":
                return t("projet.status-label.LABELLISATION_BROUILLON")
              case "INSTRUCTION_TERMINEE":
              case "INSTRUCTION_EN_COURS":
              case "EN_ATTENTE":
                if (projetAnnuel.typeDossier == "DOSSIER_LABELLISATION") {
                  return t("projet.status-label.LABELLISATION_ETUDE")
                } else {
                  return t("projet.status-label.RENOUVELLEMENT_ETUDE")
                }
              case "ACCEPTE":
                if (projetAnnuel.typeDossier == "DOSSIER_LABELLISATION") {
                  return t("projet.status-label.LABELLISATION_VALIDE")
                } else {
                  return t("projet.status-label.RENOUVELLEMENT_VALIDE")
                }
              case "LABELLISATION_EN_DEVENIR":
                return t("projet.status-label.LABELLISATION_EN_DEVENIR")
              case "REFUSE":
                return t("projet.status-label.LABELLISATION_BROUILLON")
              default:
                return projetAnnuel.statut
            }
        }

      default:
        switch (projetAnnuel.typeDossier) {
          // For accompagnateurs dossier inscription
          case "DOSSIER_INSCRIPTION":
            switch (projetAnnuel.statut) {
              case "BROUILLON":
                return t("projet.status-label.INSCRIPTION_BROUILLON")
              case "EN_ATTENTE":
                return t("projet.status-label.INSCRIPTION_EN_ATTENTE")
              case "INSTRUCTION_TERMINEE":
              case "INSTRUCTION_EN_COURS":
                return t("projet.status-label.INSCRIPTION_EN_COURS")
              case "ACCEPTE":
                return t("projet.status-label.INSCRIPTION_VALIDE")
              case "ACCEPTE_SOUS_RESERVE_REFERENT":
                return t("projet.status-label.INSCRIPTION_SOUS_RESERVE")
              case "REFUSE":
                return t("projet.status-label.INSCRIPTION_REFUSE")
              default:
                break
            }
            break

          // For accompagnateurs labellisation
          default:
            switch (projetAnnuel.statut) {
              case "BROUILLON":
                // If project was labellised in the past, show the last labellisation result
                if (
                  projetAnnuel.lastEvaluationTypeDossier &&
                  projetAnnuel.lastEvaluationTypeDossier != "DOSSIER_INSCRIPTION"
                ) {
                  // Only show detailed label if we have full DTO with evaluation details
                  if ("lastEvaluationMention" in projetAnnuel) {
                    return this.getLabelForLastLabellisation(projetAnnuel as ProjetAnnuelDTO)
                  }
                  // For light DTO, show generic labellisation label
                  return t("projet.status-label.LABELLISATION_BROUILLON")
                }
                return t("projet.status-label.LABELLISATION_BROUILLON")
              case "EN_ATTENTE":
                return t("instruction-panel.instruction.EN_ATTENTE")
              case "INSTRUCTION_TERMINEE":
                return t("instruction-panel.instruction.INSTRUCTION_TERMINEE")
              case "INSTRUCTION_EN_COURS":
                return t("instruction-panel.instruction.INSTRUCTION_EN_COURS")
              case "ACCEPTE":
                if (projetAnnuel.typeDossier == "DOSSIER_LABELLISATION") {
                  return t("projet.status-label.LABELLISATION_VALIDE")
                } else {
                  return t("projet.status-label.RENOUVELLEMENT_VALIDE")
                }
              case "LABELLISATION_EN_DEVENIR":
                return t("projet.status-label.LABELLISATION_EN_DEVENIR")
              case "REFUSE":
                if (projetAnnuel.typeDossier == "DOSSIER_LABELLISATION") {
                  return t("projet.status-label.LABELLISATION_REFUSE")
                } else {
                  return t("projet.status-label.RENOUVELLEMENT_REFUSE")
                }
              default:
                break
            }
            break
        }
    }
    return projetAnnuel.statut
  }

  getProjectConsultActionLabel(projetAnnuel: ProjetAnnuelBase, loggedUser: UtilisateurDTO): string {
    if (projetAnnuel.etat === "ARCHIVE") {
      return t("projet.consult-action-label.CONSULTER")
    }

    switch (loggedUser.profil) {
      case "REFERENT":
      case "ENSEIGNANT":
        switch (projetAnnuel.typeDossier) {
          // For PP dossier inscrition
          case "DOSSIER_INSCRIPTION":
            switch (projetAnnuel.statut) {
              case "BROUILLON":
                return t("projet.consult-action-label.INSCRIPTION_REPRENDRE")
              case "INSTRUCTION_TERMINEE":
              case "INSTRUCTION_EN_COURS":
              case "EN_ATTENTE":
              case "ACCEPTE":
              case "REFUSE":
                return t("projet.consult-action-label.INSCRIPTION_CONSULTER")
              case "ACCEPTE_SOUS_RESERVE_REFERENT":
                return t("projet.consult-action-label.INSCRIPTION_COMPLETER")
              default:
                break
            }
            break

          // For PP labellisation
          default:
            break
        }
        return t("projet.consult-action-label.CONSULTER")

      default:
        switch (projetAnnuel.typeDossier) {
          // For accompagnateurs dossier inscrition
          case "DOSSIER_INSCRIPTION":
            return t("projet.consult-action-label.INSCRIPTION_CONSULTER")

          // For accompagnateurs labellisation
          default:
            break
        }
        return t("projet.consult-action-label.CONSULTER")
    }
  }

  getLabelForLastLabellisation(projetAnnuel: ProjetAnnuelDTO): string {
    let mentionString
    switch (projetAnnuel.lastEvaluationTypeDossier) {
      case "DOSSIER_LABELLISATION":
        switch (projetAnnuel.lastEvaluationMention) {
          case "REFUSE":
            mentionString = t("projet.status-label.LABELLISATION_REFUSE")
            break
          case "LABELLISATION_EN_DEVENIR":
            mentionString = t("projet.status-label.LABELLISATION_EN_DEVENIR")
            break
          default:
            mentionString = t("projet.status-label.LABELLISATION_VALIDE")
            break
        }
        break

      default:
        switch (projetAnnuel.lastEvaluationMention) {
          case "REFUSE":
            mentionString = t("projet.status-label.RENOUVELLEMENT_REFUSE")
            break
          default:
            mentionString = t("projet.status-label.RENOUVELLEMENT_VALIDE")
            break
        }
        break
    }
    return (
      mentionString +
      " " +
      ConversionUtils.getAnneeScolaireString(
        projetAnnuel.lastEvaluationAnneeScolaireDebut,
        projetAnnuel.lastEvaluationAnneeScolaireFin,
      )
    )
  }
}
